import jsPDF from 'jspdf';
import 'jspdf-autotable';

export const generateEventReportPDF = async (eventData, stats, tenant) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Header with logos
  let yPos = 20;
  
  // ZonePass logo (text for now, can be replaced with image)
  doc.setFontSize(16);
  doc.setTextColor(88, 28, 135); // Purple
  doc.setFont(undefined, 'bold');
  doc.text('ZonePass', 20, yPos);
  
  // Tenant logo (if available)
  if (tenant?.logo) {
    try {
      doc.addImage(tenant.logo, 'PNG', pageWidth - 60, 10, 40, 20);
    } catch (e) {
      console.log('Error adding tenant logo');
    }
  }
  
  // Tenant name
  doc.setFontSize(12);
  doc.setTextColor(100);
  doc.setFont(undefined, 'normal');
  doc.text(tenant?.name || 'Organizzatore', pageWidth - 20, yPos, { align: 'right' });
  
  yPos += 15;
  
  // Divider line
  doc.setDrawColor(200);
  doc.line(20, yPos, pageWidth - 20, yPos);
  yPos += 10;
  
  // Event title
  doc.setFontSize(20);
  doc.setTextColor(0);
  doc.setFont(undefined, 'bold');
  doc.text(eventData.name, 20, yPos);
  yPos += 8;
  
  // Event date
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.setFont(undefined, 'normal');
  const startDate = new Date(eventData.start_date).toLocaleString('it-IT');
  const endDate = new Date(eventData.end_date).toLocaleString('it-IT');
  doc.text(`${startDate} - ${endDate}`, 20, yPos);
  yPos += 10;
  
  // Event description
  if (eventData.description) {
    doc.setFontSize(9);
    doc.setTextColor(80);
    const descLines = doc.splitTextToSize(eventData.description, pageWidth - 40);
    doc.text(descLines, 20, yPos);
    yPos += descLines.length * 5 + 5;
  }
  
  yPos += 5;
  
  // Summary statistics boxes
  doc.setFillColor(240, 240, 250);
  doc.roundedRect(20, yPos, 45, 25, 3, 3, 'F');
  doc.roundedRect(70, yPos, 45, 25, 3, 3, 'F');
  doc.roundedRect(120, yPos, 45, 25, 3, 3, 'F');
  
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text('Partecipanti', 22, yPos + 8);
  doc.text('Attualmente Dentro', 72, yPos + 8);
  doc.text('Totale Scan', 122, yPos + 8);
  
  doc.setFontSize(16);
  doc.setTextColor(88, 28, 135);
  doc.setFont(undefined, 'bold');
  doc.text(stats?.total_participants?.toString() || '0', 22, yPos + 18);
  doc.text(stats?.currently_inside?.toString() || '0', 72, yPos + 18);
  
  const totalScans = stats?.participants 
    ? Object.values(stats.participants).reduce((sum, p) => sum + p.total_entries + p.total_exits, 0) 
    : 0;
  doc.text(totalScans.toString(), 122, yPos + 18);
  
  yPos += 35;
  
  // Detailed participants table
  if (stats?.participants && Object.keys(stats.participants).length > 0) {
    doc.setFont(undefined, 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text('Dettaglio Partecipanti', 20, yPos);
    yPos += 5;
    
    const tableData = Object.values(stats.participants).map(p => [
      p.holder_name,
      p.holder_email,
      p.total_entries,
      p.total_exits,
      p.currently_inside ? 'Sì' : 'No',
      p.first_entry ? new Date(p.first_entry).toLocaleString('it-IT', { 
        day: '2-digit', 
        month: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      }) : '-'
    ]);
    
    doc.autoTable({
      startY: yPos,
      head: [['Nome', 'Email', 'Entrate', 'Uscite', 'Dentro', 'Primo Ingresso']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [88, 28, 135], textColor: 255 },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 50 },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' },
        4: { cellWidth: 20, halign: 'center' },
        5: { cellWidth: 30 }
      }
    });
    
    yPos = doc.lastAutoTable.finalY + 10;
  }
  
  // Footer
  const footerY = pageHeight - 15;
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.setFont(undefined, 'normal');
  doc.text('Generato da ZonePass - Sistema di Gestione Eventi', pageWidth / 2, footerY, { align: 'center' });
  doc.text(`Report generato il ${new Date().toLocaleString('it-IT')}`, pageWidth / 2, footerY + 4, { align: 'center' });
  
  // Save PDF
  const fileName = `report-${eventData.name.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
  doc.save(fileName);
  
  return fileName;
};

export const generateGuestListPDF = async (guests, eventData, tenant) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFontSize(16);
  doc.setTextColor(88, 28, 135);
  doc.setFont(undefined, 'bold');
  doc.text('ZonePass', 20, 20);
  
  if (tenant?.name) {
    doc.setFontSize(12);
    doc.text(tenant.name, pageWidth - 20, 20, { align: 'right' });
  }
  
  let yPos = 35;
  
  doc.setFontSize(18);
  doc.setTextColor(0);
  doc.text(`Guest List - ${eventData.name}`, 20, yPos);
  yPos += 10;
  
  // Guest table
  const tableData = guests.map(g => [
    `${g.first_name} ${g.last_name}`,
    g.email,
    g.phone,
    g.ticket_type?.name || '-',
    g.payment_status === 'paid' ? 'Pagato' : g.payment_status === 'pending' ? 'Pending' : 'Gratuito'
  ]);
  
  doc.autoTable({
    startY: yPos,
    head: [['Nome', 'Email', 'Telefono', 'Tipo Biglietto', 'Status']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [88, 28, 135], textColor: 255 },
    styles: { fontSize: 9 }
  });
  
  const fileName = `guest-list-${eventData.name.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
  doc.save(fileName);
  
  return fileName;
};
